/*
 * COPYRIGHT Motorola Solutions, INC.
 * ALL RIGHTS RESERVED.
 * MOTOROLA SOLUTIONS CONFIDENTIAL RESTRICTED
 */

import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
    deleteCall,
    initializeCallState,
    newCall,
    updateActiveCall,
    updateCall,
    updateCallMultipleSubscriptions,
    updateSelectedCall
} from '../+state/call.actions';
import { Call, ExpectedCall, UIDialSource } from 'CalltakingCoreApi';
import { HttpClient, HttpParams } from '@angular/common/http';
import { filter, take } from 'rxjs/operators';
import {
    selectACDAnswerSuccess,
    selectAcknowledgeAbandonedCallComplete,
    selectComputeActiveCallId,
    selectHoldComplete,
    selectMultipleCallStateInitializationTime,
    selectReleaseSuccess
} from '../+state/call.selectors';
import { selectComputeSelectedCallId } from '../../main/+state/main.selectors';
import { SupervisorSettings } from '../../configuration/model/ui-settings';
import { ClusterUrlProviderService } from '../../core/services/cluster-url-provider.service';
import { selectClusterConfigurationMap } from '../../configuration/+state/configuration.selectors';
import { StompManagerService } from '../../core/services/stomp-manager/stomp-manager.service';
import { StompBaseConsumer } from '../../core/abstract/stomp-base-consumer';
import { Topic } from '../../user/model/multiple-subscriptions';

@Injectable({
    providedIn: 'root'
})
export class CallService extends StompBaseConsumer {
    private readonly API_ICC_URL = `/api/icc/v1/`;
    private readonly API_IMC_URL = `/api/imc/v1/`;

    constructor(
        private http: HttpClient,
        private clusterUrlProviderService: ClusterUrlProviderService,
        protected store: Store,
        protected stompManager: StompManagerService
    ) {
        super(stompManager, store);
            // Trigger (re)initialization of each cluster's call state
            this.store.select(selectClusterConfigurationMap)
                .pipe(filter((clusterMap) => Boolean(Object.keys(clusterMap).length)), take(1))
                .subscribe((clusterMap) =>
                    Object.keys(clusterMap).forEach((clusterName) =>
                        this.store.select(selectMultipleCallStateInitializationTime(clusterName))
                            .pipe(
                                filter((init) => !!init)
                            ).subscribe((init) => {
                            this.store.dispatch(initializeCallState({
                                clusterName: clusterName,
                                initializationTime: init
                            }));
                        })));

            // Compute and set activeCallId
            this.store.select(selectComputeActiveCallId).subscribe((activeCallId) => this.store.dispatch(updateActiveCall({ callId: activeCallId })));

            // Compute and set selectedCallId
            this.store.select(selectComputeSelectedCallId).subscribe((selectedCallId) => this.store.dispatch(updateSelectedCall({ callId: selectedCallId })));

            // Monitor for Hold Complete
            this.store
                .select(selectHoldComplete)
                .pipe(filter((val) => !!val))
                .subscribe((action) => this.store.dispatch(action));

            // Monitor for Release Success
            this.store
                .select(selectReleaseSuccess)
                .pipe(filter((val) => !!val))
                .subscribe((action) => this.store.dispatch(action));

            // Monitor for Answer Success and dispatch completion event
            this.store
                .select(selectACDAnswerSuccess)
                .pipe(filter((val) => !!val))
                .subscribe((action) => this.store.dispatch(action));

            this.store
                .select(selectAcknowledgeAbandonedCallComplete)
                .pipe(filter((val) => !!val))
                .subscribe((action) => this.store.dispatch(action));
    }

    protected handleMessageCallNew(call: Call) {
        this.store.dispatch(newCall({ call }));
    }

    protected handleMessageCallEdit(call: Call) {
        this.store.dispatch(updateCall({ call }));
    }

    protected handleMessageCallDelete(call: Call) {
        this.store.dispatch(deleteCall({ call }));
    }

    protected getTopics(): Topic[] {
        return [Topic.CALL_NEW, Topic.CALL_EDIT, Topic.CALL_DELETE];
    }

    protected getTopicHandlers(): { [p in Topic]?: (message: any) => void } {
        return {
            [Topic.CALL_NEW]: this.handleMessageCallNew.bind(this),
            [Topic.CALL_EDIT]: this.handleMessageCallEdit.bind(this),
            [Topic.CALL_DELETE]: this.handleMessageCallDelete.bind(this)
        };
    }

    protected getTopicDelays(): { [topic: string]: number } {
        return {
            [Topic.CALL_DELETE]: 1000
        };
    }

    handleSubscriptionInit(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} Call topic ${topic} subscription success: ${receiptId}`);
        this.store.dispatch(updateCallMultipleSubscriptions({ topic: topic as Topic, clusterName, status: true }));
    }

    handleSubscriptionEnd(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} Call topic ${topic} subscription end: ${receiptId}`);
        this.store.dispatch(updateCallMultipleSubscriptions({ topic: topic as Topic, clusterName, status: false }));
    }

    handleStompError(topic: string, receiptId: string, error: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} Call topic ${topic} subscription failed=${error} for receiptId=${receiptId}`);
    }


    public requestCalls(clusterName: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}load`);
        return this.http.get<Call[]>(url, { observe: 'body', responseType: 'json' });
    }

    public answer(callId: string, clusterName: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/answer`);
        return this.http.post(url, {});
    }

    public join(callId: string, clusterName: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/barge-in`);
        return this.http.post(url, {});
    }

    public silentMonitor(callId: string, clusterName: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/silent-monitor`);
        return this.http.post(url, {});
    }

    public dial(number: string, clusterName: string, source: UIDialSource, label?: string, id?: string, destinations?: string[]) {
        let params = new HttpParams();
        params = label ? params.append('label', label) : params;
        params = id ? params.append('directoryContactId', id) : params;
        params = source ? params.append('uiDialSource', source) : params;
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}dial/${encodeURIComponent(number)}`);
        return this.http.post<ExpectedCall>(url, { 'destinations': destinations }, {
            params: params,
            observe: 'body',
            responseType: 'json'
        });
    }

    public conference(
        clusterName: string,
        callId: string,
        number: string,
        source: UIDialSource,
        label?: string,
        id?: string,
        blindTransfer?: boolean,
        supervisorAssistance?: SupervisorSettings,
        destinations?: string[]
    ) {
        let params = new HttpParams();
        params = label ? params.append('label', label) : params;
        params = id ? params.append('directoryContactId', id) : params;
        params = source ? params.append('uiDialSource', source) : params;
        params = blindTransfer ? params.append('blindTransfer', blindTransfer) : params;
        params = supervisorAssistance ? params.append('supervisorAssistance', true) : params;
        params = supervisorAssistance ? params.append('deafen', supervisorAssistance.callerDeafened) : params;
        params = supervisorAssistance ? params.append('suppressRingback', !supervisorAssistance.ringback) : params;

        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/conference/${encodeURIComponent(number)}`);
        return this.http.post(url, { 'destinations': destinations }, { params: params });
    }

    public volume(clusterName: string, callId: string, participantId: string, volume?: number, gain?: number) {
        let params = new HttpParams();
        params = volume ? params.append('volume', String(volume)) : params;
        params = gain ? params.append('gain', String(gain)) : params;

        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/volume/${participantId}`);
        return this.http.post(url, {}, { params: params });
    }

    public hookFlash(clusterName: string, callId: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/hook-flash`);
        return this.http.post(url, {});
    }

    public mute(callId: string, clusterName: string, participantId: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/mute/${participantId}`);
        return this.http.post(url, {});
    }

    public unmute(callId: string, clusterName: string, participantId: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/unmute/${participantId}`);
        return this.http.post(url, {});
    }

    public deafen(callId: string, clusterName: string, participantId: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/deafen/${participantId}`);
        return this.http.post(url, {});
    }

    public undeafen(callId: string, clusterName: string, participantId: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/undeafen/${participantId}`);
        return this.http.post(url, {});
    }

    public redial(callId: string, clusterName: string, number: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/redial/${encodeURIComponent(number)}`);
        return this.http.post<ExpectedCall>(url, {});
    }

    public redialAbandoned(callId: string, clusterName: string, number?: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/redial-abandoned`);
        let params = new HttpParams();
        params = number ? params.append('number', number) : params;
        return this.http.post(url, {}, { params: params });
    }

    public release(callId: string, clusterName: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/release`);
        return this.http.post(url, {});
    }

    public releaseParticipant(callId: string, clusterName: string, participantId: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/release/${participantId}`);
        return this.http.post(url, {});
    }

    public releasePendingParticipant(callId: string, clusterName: string, participantId: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/release-dialed-contact/${participantId}`);
        return this.http.post(url, {});
    }

    public releaseNetworkParticipant(callId: string, clusterName: string, participantId: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/network-release/${participantId}`);
        return this.http.post(url, {});
    }

    public hold(callId: string, clusterName: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/hold`);
        return this.http.post(url, {});
    }

    public unhold(callId: string, clusterName: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/unhold`);
        return this.http.post(url, {});
    }

    public tddMessage(callId: string, clusterName: string | undefined, message: string, predefined?: boolean, language?: string, clientMessageId?: string,
                      sourceText?: string, sourceLanguage?: string, targetText?: string, targetLanguage?: string, messageLanguage?: string) {
        let params = new HttpParams();
        params = predefined ? params.append('predefined', predefined) : params;
        params = language ? params.append('predefinedLanguage', language) : params;
        params = clientMessageId ? params.append('clientMessageId', clientMessageId) : params;
        params = sourceText ? params.append('sourceText', sourceText) : params;
        params = sourceLanguage ? params.append('sourceLanguage', sourceLanguage) : params;
        params = targetText ? params.append('targetText', targetText) : params;
        params = targetLanguage ? params.append('targetLanguage', targetLanguage) : params;
        params = messageLanguage ? params.append('messageLanguage', messageLanguage) : params;

        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/send-tdd-message`);
        return this.http.post(url, message, { params: params });
    }

    public rttMessage(callId: string, clusterName: string | undefined, message: string, predefined?: boolean, language?: string) {
        let params = new HttpParams();
        params = predefined ? params.append('predefined', predefined) : params;
        params = language ? params.append('predefinedLanguage', language) : params;

        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/send-rtt-message`);
        return this.http.post(url, message, { params: params });
    }

    public smsMessage(callId: string, clusterName: string | undefined, message: string, predefined?: boolean, language?: string, clientMessageId?: string,
                      sourceText?: string, sourceLanguage?: string, targetText?: string, targetLanguage?: string, messageLanguage?: string) {
        let params = new HttpParams();
        params = predefined ? params.append('predefined', predefined) : params;
        params = language ? params.append('predefinedLanguage', language) : params;
        params = clientMessageId ? params.append('clientMessageId', clientMessageId) : params;
        params = sourceText ? params.append('sourceText', sourceText) : params;
        params = sourceLanguage ? params.append('sourceLanguage', sourceLanguage) : params;
        params = targetText ? params.append('targetText', targetText) : params;
        params = targetLanguage ? params.append('targetLanguage', targetLanguage) : params;
        params = messageLanguage ? params.append('messageLanguage', messageLanguage) : params;

        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_IMC_URL}call/${callId}/send-sms`);
        return this.http.post(url, { message: message }, { params: params });
    }

    public dtmfMessage(callId: string, clusterName: string | undefined, message: string) {
        let params = new HttpParams();
        params = message ? params.append('digit', String(message)) : params;

        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/send-dtmf`);
        return this.http.post(url, {}, { params: params });
    }

    public supervisor(callId: string, clusterName: string | undefined) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/supervisor`);
        return this.http.post(url, {});
    }

    public createTextCall(number: string, clusterName: string, source?: UIDialSource) {
        let params = new HttpParams();
        params = source ? params.append('uiDialSource', source) : params;
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}create-text-call/${number}`);
        return this.http.post(url, {}, { params: params });
    }

    public acknowledgeAbandonedCall(callId: string, clusterName: string | undefined) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/acknowledge-abandoned`);
        return this.http.post(url, {});
    }

    public refuseAbandonedCall(callId: string, clusterName: string | undefined) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_ICC_URL}${callId}/refuse-abandoned`);
        return this.http.post(url, {});
    }
}
